/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

export const GUIDED_LEARNING_RUNTIME_SCHEMA_VERSION = 1 as const;
export const GUIDED_LEARNING_TARGET_ATTRIBUTE = 'data-qp-learning-id' as const;

export const GUIDED_LEARNING_RUNTIME_LOCALES = [
  'tr', 'en', 'ar', 'de', 'fr', 'es', 'it', 'ru', 'zh', 'ja',
] as const;

export const GUIDED_LEARNING_RUNTIME_MODES = ['DEMO', 'COACH', 'CHALLENGE'] as const;
export const GUIDED_LEARNING_RUNTIME_CAPABILITIES = [
  'FOCUS', 'SET_VALUE', 'SELECT', 'ACTIVATE',
] as const;
export const GUIDED_LEARNING_RUNTIME_STEP_KINDS = [
  'NAVIGATE', 'EXPLAIN', 'INPUT', 'SELECT', 'ACTIVATE', 'CHECKPOINT',
] as const;

export type GuidedLearningRuntimeLocale = (typeof GUIDED_LEARNING_RUNTIME_LOCALES)[number];
export type GuidedLearningRuntimeMode = (typeof GUIDED_LEARNING_RUNTIME_MODES)[number];
export type GuidedLearningRuntimeCapability = (typeof GUIDED_LEARNING_RUNTIME_CAPABILITIES)[number];
export type GuidedLearningRuntimeStepKind = (typeof GUIDED_LEARNING_RUNTIME_STEP_KINDS)[number];

export type GuidedLearningRuntimeValue =
  | { kind: 'LITERAL'; value: string }
  | { kind: 'RUN_SHORT_ID' }
  | { kind: 'SEQUENCE' }
  | { kind: 'TODAY' }
  | { kind: 'DATE_PLUS'; days: number }
  | { kind: 'SYNTHETIC_EMAIL' }
  | { kind: 'SYNTHETIC_PHONE' }
  | { kind: 'FIXTURE'; key: string };

export interface GuidedLearningRuntimeStep {
  id: string;
  kind: GuidedLearningRuntimeStepKind;
  title: string;
  instruction: string;
  narration?: string;
  hint?: string;
  audioPath?: string;
  routeKey?: string;
  routePath?: string;
  targetKey?: string;
  capability?: GuidedLearningRuntimeCapability;
  optionKey?: string;
  resolvedValue?: string;
  checkpointKey?: string;
  timeoutMs: number;
  optionalInPractice: boolean;
}

export interface GuidedLearningRuntimePayload {
  schemaVersion: typeof GUIDED_LEARNING_RUNTIME_SCHEMA_VERSION;
  runRef: string;
  applicationCode: string;
  scenarioVersionRef: string;
  scenarioDigest: string;
  contractSubsetDigest: string;
  mode: GuidedLearningRuntimeMode;
  locale: GuidedLearningRuntimeLocale;
  currentStepIndex: number;
  steps: GuidedLearningRuntimeStep[];
  challengeTitle?: string;
  challengeTasks?: string[];
}

export interface GuidedLearningSemanticEvent {
  targetKey: string;
  capability: GuidedLearningRuntimeCapability;
  optionKey?: string;
  occurredAt: string;
}

const TECHNICAL_KEY_PATTERN = /^[a-z][a-z0-9]*(?:[._-][a-z0-9]+){1,15}$/;
const APP_CODE_PATTERN = /^[A-Z][A-Z0-9_]{1,31}$/;
const OPAQUE_REF_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{7,159}$/;
const SHA256_PATTERN = /^[a-f0-9]{64}$/;
const SAME_ORIGIN_PATH_PATTERN = /^\/api\/[a-zA-Z0-9/_?&=.%~-]{1,240}$/;
const PRODUCT_PATH_PATTERN = /^\/[a-z0-9][a-z0-9/_-]{0,239}$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function optionalText(value: unknown, max: number): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value !== 'string') return undefined;
  const text = value.trim();
  return text && text.length <= max ? text : undefined;
}

function parseRuntimeStep(value: unknown): GuidedLearningRuntimeStep | null {
  if (!isRecord(value)) return null;
  const id = optionalText(value.id, 128);
  const title = optionalText(value.title, 240);
  const instruction = optionalText(value.instruction, 4000);
  const kind = String(value.kind ?? '') as GuidedLearningRuntimeStepKind;
  const timeoutMs = Number(value.timeoutMs);
  if (
    !id || !TECHNICAL_KEY_PATTERN.test(id)
    || !title || !instruction
    || !(GUIDED_LEARNING_RUNTIME_STEP_KINDS as readonly string[]).includes(kind)
    || !Number.isSafeInteger(timeoutMs) || timeoutMs < 1000 || timeoutMs > 30000
    || typeof value.optionalInPractice !== 'boolean'
  ) return null;

  const routeKey = optionalText(value.routeKey, 160);
  const routePath = optionalText(value.routePath, 240);
  const targetKey = optionalText(value.targetKey, 180);
  const capability = optionalText(value.capability, 32) as GuidedLearningRuntimeCapability | undefined;
  const optionKey = optionalText(value.optionKey, 180);
  const checkpointKey = optionalText(value.checkpointKey, 160);
  const resolvedValue = optionalText(value.resolvedValue, 500);
  const audioPath = optionalText(value.audioPath, 256);
  if (routeKey && !TECHNICAL_KEY_PATTERN.test(routeKey)) return null;
  if (routePath && (!PRODUCT_PATH_PATTERN.test(routePath) || routePath.includes('..'))) return null;
  if (targetKey && !TECHNICAL_KEY_PATTERN.test(targetKey)) return null;
  if (optionKey && !TECHNICAL_KEY_PATTERN.test(optionKey)) return null;
  if (checkpointKey && !TECHNICAL_KEY_PATTERN.test(checkpointKey)) return null;
  if (audioPath && !SAME_ORIGIN_PATH_PATTERN.test(audioPath)) return null;
  if (capability && !(GUIDED_LEARNING_RUNTIME_CAPABILITIES as readonly string[]).includes(capability)) return null;

  if (kind === 'NAVIGATE' && (!routeKey || !routePath)) return null;
  if ((kind === 'INPUT' || kind === 'SELECT' || kind === 'ACTIVATE') && (!targetKey || !capability)) return null;
  if (kind === 'INPUT' && (capability !== 'SET_VALUE' || resolvedValue === undefined)) return null;
  if (kind === 'SELECT' && (capability !== 'SELECT' || !optionKey)) return null;
  if (kind === 'ACTIVATE' && capability !== 'ACTIVATE') return null;
  if (kind === 'CHECKPOINT' && !checkpointKey) return null;
  if (value.audioPath !== undefined && !audioPath) return null;
  if (value.narration !== undefined && !optionalText(value.narration, 4000)) return null;
  if (value.hint !== undefined && !optionalText(value.hint, 2000)) return null;

  return {
    id,
    kind,
    title,
    instruction,
    timeoutMs,
    optionalInPractice: value.optionalInPractice,
    ...(optionalText(value.narration, 4000) ? { narration: optionalText(value.narration, 4000) } : {}),
    ...(optionalText(value.hint, 2000) ? { hint: optionalText(value.hint, 2000) } : {}),
    ...(audioPath ? { audioPath } : {}),
    ...(routeKey ? { routeKey } : {}),
    ...(routePath ? { routePath } : {}),
    ...(targetKey ? { targetKey } : {}),
    ...(capability ? { capability } : {}),
    ...(optionKey ? { optionKey } : {}),
    ...(resolvedValue !== undefined ? { resolvedValue } : {}),
    ...(checkpointKey ? { checkpointKey } : {}),
  };
}

export function parseGuidedLearningRuntimePayload(value: unknown): GuidedLearningRuntimePayload | null {
  if (!isRecord(value) || value.schemaVersion !== GUIDED_LEARNING_RUNTIME_SCHEMA_VERSION) return null;
  const runRef = optionalText(value.runRef, 160);
  const applicationCode = optionalText(value.applicationCode, 32)?.toUpperCase();
  const scenarioVersionRef = optionalText(value.scenarioVersionRef, 160);
  const scenarioDigest = optionalText(value.scenarioDigest, 64);
  const contractSubsetDigest = optionalText(value.contractSubsetDigest, 64);
  const mode = String(value.mode ?? '') as GuidedLearningRuntimeMode;
  const locale = String(value.locale ?? '') as GuidedLearningRuntimeLocale;
  const currentStepIndex = Number(value.currentStepIndex);
  if (
    !runRef || !OPAQUE_REF_PATTERN.test(runRef)
    || !applicationCode || !APP_CODE_PATTERN.test(applicationCode)
    || !scenarioVersionRef || !OPAQUE_REF_PATTERN.test(scenarioVersionRef)
    || !scenarioDigest || !SHA256_PATTERN.test(scenarioDigest)
    || !contractSubsetDigest || !SHA256_PATTERN.test(contractSubsetDigest)
    || !(GUIDED_LEARNING_RUNTIME_MODES as readonly string[]).includes(mode)
    || !(GUIDED_LEARNING_RUNTIME_LOCALES as readonly string[]).includes(locale)
    || !Array.isArray(value.steps) || value.steps.length === 0 || value.steps.length > 100
    || !Number.isSafeInteger(currentStepIndex) || currentStepIndex < 0 || currentStepIndex >= value.steps.length
  ) return null;
  const steps = value.steps.map(parseRuntimeStep);
  if (steps.some((step) => step === null)) return null;
  const challengeTasks = value.challengeTasks === undefined
    ? undefined
    : Array.isArray(value.challengeTasks)
      ? value.challengeTasks.map((item) => optionalText(item, 500)).filter((item): item is string => Boolean(item))
      : null;
  if (
    challengeTasks === null
    || (challengeTasks !== undefined && challengeTasks.length > 20)
    || (challengeTasks !== undefined && challengeTasks.length !== (value.challengeTasks as unknown[]).length)
  ) {
    return null;
  }
  const challengeTitle = optionalText(value.challengeTitle, 240);
  return {
    schemaVersion: GUIDED_LEARNING_RUNTIME_SCHEMA_VERSION,
    runRef,
    applicationCode,
    scenarioVersionRef,
    scenarioDigest,
    contractSubsetDigest,
    mode,
    locale,
    currentStepIndex,
    steps: steps as GuidedLearningRuntimeStep[],
    ...(challengeTitle ? { challengeTitle } : {}),
    ...(challengeTasks ? { challengeTasks } : {}),
  };
}

export function guidedLearningTargetSelector(targetKey: string): string {
  if (!TECHNICAL_KEY_PATTERN.test(targetKey)) return '';
  return `[${GUIDED_LEARNING_TARGET_ATTRIBUTE}="${targetKey}"]`;
}
